import type { ReactNode } from 'react';
import { PlugZap } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { reportBreadcrumbItems } from '@/components/PageBreadcrumbs';
import { usePermission } from '@/hooks/usePermission';
import { REPORT_FEATURES, missingReportFeatures, reportById } from '@/reports/catalog';
import { useReportFeatures } from '@/reports/use-report-features';

/**
 * A report's page: the report when the team has every feature it needs (`requires` in the
 * catalog), otherwise a short note saying what it needs, in the manner of NotConnectedState
 * (a link to the setting for people who may change it, "ask an admin" for everyone else). The
 * Reports list and search already leave such a report out; this is for a link or bookmark.
 */
export function ReportFeatureGate({ reportId, children }: { reportId: string; children: ReactNode }) {
  const features = useReportFeatures();
  const canSetUp = usePermission('SYNC_SETUP');
  const report = reportById(reportId);
  const missing = report ? missingReportFeatures(report, features) : [];
  if (!report || missing.length === 0) return <>{children}</>;

  const needs = missing.map((feature) => REPORT_FEATURES[feature].label).join(' and ');
  const isOrAre = missing.length > 1 ? 'are' : 'is';
  const first = REPORT_FEATURES[missing[0]];
  return (
    <div className="space-y-6">
      <PageHeader breadcrumbItems={reportBreadcrumbItems(report.name)} />
      <EmptyState
        icon={<PlugZap className="size-5" />}
        title={`${report.name} needs ${needs}`}
        description={
          canSetUp
            ? `${needs} ${isOrAre} off for this team, so this report has nothing to show.`
            : `${needs} ${isOrAre} off for this team, so this report has nothing to show. An admin can turn it on in Integrations.`
        }
        action={
          canSetUp
            ? { label: `${first.label} settings`, href: first.settingsPath }
            : { label: 'Back to Reports', href: '/reports' }
        }
      />
    </div>
  );
}
